import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  UserGroupIcon, 
  AcademicCapIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import { getApiUrl } from '../utils/api';

const Youth = () => {
  const location = useLocation();
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);

  // Auto-show registration form if accessed via /register route
  useEffect(() => {
    if (location.pathname === '/register') {
      setShowRegistrationForm(true);
      // Scroll to registration section after a short delay to ensure form is rendered
      setTimeout(() => {
        const registrationSection = document.getElementById('registration-section');
        if (registrationSection) {
          registrationSection.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }, [location.pathname]);
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    birthDate: '',
    ageGroup: '',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    address: '',
    medicalInfo: ''
  });
  const ageGroups = [
    { name: "U-9", description: "9 წლამდე", players: 2, coaches: ["მევლუდი ლომიძე", "ვახტანგ ჯაფარაშვილი"], link: "/youth/u9" },
    { name: "U-10", description: "10 წლამდე", players: 1, coaches: ["ლევან ლომიძე"], link: "/youth/u10" },
    { name: "U-11", description: "11 წლამდე", players: 1, coaches: ["გიორგი ჩადუნელი"], link: "/youth/u11" },
    { name: "U-12", description: "12 წლამდე", players: 1, coaches: ["დავით ტაბატაძე"], link: "/youth/u12" },
    { name: "U-13", description: "13 წლამდე", players: 1, coaches: ["ავთანდილ ლომიძე"], link: "/youth/u13" },
    { name: "U-14", description: "14 წლამდე", players: 1, coaches: ["ვეფხვია გელაშვილი"], link: "/youth/u14" },
    { name: "U-15", description: "15 წლამდე", players: 1, coaches: ["ბესო ვეშაპიძე"], link: "/youth/u15" },
    { name: "U-17", description: "17 წლამდე", players: 2, coaches: ["ზურა ლომიძე", "ზაზა ლაცაბიძე"], link: "/youth/u17" },
    { name: "ქალთა გუნდი", description: "ქალთა საფეხბურთო გუნდი", players: 16, coaches: ["ივანე ხარაზიშვილი", "ირმა ლომიძე"], link: "/youth/womens-team" }
  ];

  const coachingStaff = [
    // U9 Coaches
    { name: "მევლუდი ლომიძე", position: "U9 მწვრთნელი", age: 65, experience: "40 წელი", license: "C", ageGroup: "U9" },
    { name: "ვახტანგ ჯაფარაშვილი", position: "U9 მწვრთნელი", age: 44, experience: "20 წელი", license: "C", ageGroup: "U9" },
    
    // U10 Coach
    { name: "ლევან ლომიძე", position: "U10 მწვრთნელი", age: 37, experience: "11 წელი", license: "C", ageGroup: "U10" },
    
    // U11 Coach
    { name: "გიორგი ჩადუნელი", position: "U11 მწვრთნელი", age: 53, experience: "8 წელი", license: "C", ageGroup: "U11" },
    
    // U12 Coach
    { name: "დავით ტაბატაძე", position: "U12 მწვრთნელი", age: 29, experience: "5 წელი", license: "C", ageGroup: "U12" },
    
    // U13 Coach
    { name: "ავთანდილ ლომიძე", position: "U13 მწვრთნელი", age: 65, experience: "20 წელი", license: "C", ageGroup: "U13" },
    
    // U14 Coach
    { name: "ვეფხვია გელაშვილი", position: "U14 მწვრთნელი", age: 45, experience: "20 წელი", license: "C", ageGroup: "U14" },
    
    // U15 Coach
    { name: "ბესო ვეშაპიძე", position: "U15 მწვრთნელი", age: 48, experience: "20 წელი", license: "C", ageGroup: "U15" },
    
    // U17 Coaches
    { name: "ზურა ლომიძე", position: "U17 მწვრთნელი", age: 38, experience: "8 წელი", license: "A", ageGroup: "U17" },
    { name: "ზაზა ლაცაბიძე", position: "U17 მწვრთნელი", age: 50, experience: "12 წელი", license: "C", ageGroup: "U17" },
    
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(getApiUrl('/api/registration/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(result.message);
        setShowRegistrationForm(false);
        setRegistrationData({
          firstName: '',
          lastName: '',
          birthDate: '',
          ageGroup: '',
          parentName: '',
          parentPhone: '',
          parentEmail: '',
          address: '',
          medicalInfo: ''
        });
      } else {
        alert(result.error || 'შეცდომა რეგისტრაციის გაგზავნისას. გთხოვთ სცადოთ მოგვიანებით.');
      }
    } catch (error) {
      console.error('Error submitting registration:', error);
      alert('შეცდომა რეგისტრაციის გაგზავნისას. გთხოვთ სცადოთ მოგვიანებით.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            ასაკობრივი გუნდები
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი ახალგაზრდული აკადემია აღზრდის მომავალი ვარსკვლავების
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-lg shadow-lg p-2">
            <div className="flex space-x-2">
              <Link
                to="/youth/coaches"
                className="flex items-center px-4 py-2 rounded-lg bg-primary-600 text-white font-georgian"
              >
                <AcademicCapIcon className="h-5 w-5 mr-2" />
                მწვრთნელთა კორპუსი
              </Link>
              <Link
                to="/youth/staff"
                className="flex items-center px-4 py-2 rounded-lg text-gray-700 hover:bg-gray-100 font-georgian"
              >
                <UserGroupIcon className="h-5 w-5 mr-2" />
                პერსონალი
              </Link>
            </div>
          </div>
        </div>

        {/* Age Groups */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            ასაკობრივი ჯგუფები
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ageGroups.map((group, index) => (
              <Link key={index} to={group.link} className="block">
                <div className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100 transition-colors">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
                      {group.name}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                      {group.description}
                    </h3>
                    <p className="text-sm text-gray-600 font-georgian mb-2">
                      {group.players} ფეხბურთელი
                    </p>
                    <p className="text-xs text-gray-500 font-georgian">
                      {group.coaches.length} მწვრთნელი
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Coaching Staff Preview */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            მწვრთნელთა კორპუსი
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coachingStaff.map((coach, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold">
                    {coach.name.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 font-georgian">{coach.name}</h4>
                    <p className="text-sm text-gray-600 font-georgian">{coach.position}</p>
                    <p className="text-xs text-gray-500 font-georgian">{coach.experience} • {coach.license} ლიცენზია</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/youth/coaches"
              className="inline-flex items-center bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
            >
              სრული მწვრთნელთა კორპუსი
            </Link>
          </div>
        </div>

        {/* Academy Info */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6">
            ახალგაზრდული აკადემია
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-4">
                ჩვენი მისია
              </h3>
              <p className="text-gray-600 font-georgian leading-relaxed">
                FC ივერიას ახალგაზრდული აკადემია მიზნად ისახავს ახალგაზრდა 
                ფეხბურთელების აღზრდას, მათი ტექნიკური და ტაქტიკური უნარების 
                განვითარებას, ასევე ღირებულებების ჩამოყალიბებას.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-4">
                ჩვენი მიღწევები
              </h3>
              <ul className="space-y-2 text-gray-600 font-georgian">
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                  25+ ახალგაზრდა ფეხბურთელი
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                  9 ასაკობრივი ჯგუფი
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                  18+ პროფესიონალური მწვრთნელი
                </li>
                <li className="flex items-center">
                  <span className="w-2 h-2 bg-primary-600 rounded-full mr-3"></span>
                  ქალთა საფეხბურთო გუნდი
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Academy Registration */}
        <div id="registration-section" className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-4 flex items-center justify-center">
              <UserPlusIcon className="h-6 w-6 mr-3" />
              აკადემიაში რეგისტრაცია
            </h2>
            <p className="text-gray-600 font-georgian mb-6">
              შეავსეთ ფორმა და გახდით ჩვენი აკადემიის წევრი
            </p>
            <button
              onClick={() => setShowRegistrationForm(!showRegistrationForm)}
              className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 font-georgian"
            >
              {showRegistrationForm ? 'ფორმის დახურვა' : 'რეგისტრაციის დაწყება'}
            </button>
          </div>

          {showRegistrationForm && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ბავშვის სახელი *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={registrationData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                    placeholder="შეიყვანეთ ბავშვის სახელი"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ბავშვის გვარი *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={registrationData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                    placeholder="შეიყვანეთ ბავშვის გვარი"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    დაბადების თარიღი *
                  </label>
                  <input
                    type="date"
                    name="birthDate"
                    value={registrationData.birthDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    ასაკობრივი ჯგუფი *
                  </label>
                  <select
                    name="ageGroup"
                    value={registrationData.ageGroup}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  >
                    <option value="">აირჩიეთ ასაკობრივი ჯგუფი</option>
                    <option value="U-9">U-9 (9 წლამდე)</option>
                    <option value="U-11">U-11 (11 წლამდე)</option>
                    <option value="U-13">U-13 (13 წლამდე)</option>
                    <option value="U-15">U-15 (15 წლამდე)</option>
                    <option value="U-17">U-17 (17 წლამდე)</option>
                    <option value="U-19">U-19 (19 წლამდე)</option>
                    <option value="ქალთა გუნდი">ქალთა გუნდი</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    მშობლის სახელი და გვარი *
                  </label>
                  <input
                    type="text"
                    name="parentName"
                    value={registrationData.parentName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                    placeholder="შეიყვანეთ მშობლის სახელი და გვარი"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                    მშობლის ტელეფონი *
                  </label>
                  <input
                    type="tel"
                    name="parentPhone"
                    value={registrationData.parentPhone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                    placeholder="+995 599 123 456"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  მშობლის ელ. ფოსტა *
                </label>
                <input
                  type="email"
                  name="parentEmail"
                  value={registrationData.parentEmail}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  მისამართი
                </label>
                <input
                  type="text"
                  name="address"
                  value={registrationData.address}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  placeholder="შეიყვანეთ სრული მისამართი"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 font-georgian mb-2">
                  სამედიცინო ინფორმაცია
                </label>
                <textarea
                  name="medicalInfo"
                  value={registrationData.medicalInfo}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 font-georgian"
                  placeholder="შეიყვანეთ ნებისმიერი სამედიცინო ინფორმაცია ან ალერგიები"
                ></textarea>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 font-georgian"
                >
                  რეგისტრაციის გაგზავნა
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Youth;
